import { Resource } from '@modelcontextprotocol/sdk/types.js';
import type { Config } from '../types/index.js';
import { detectProject, findSmartStackStructure } from '../lib/detector.js';
import { findFiles } from '../utils/fs.js';
import path from 'path';

export const projectInfoResourceTemplate: Resource = {
  uri: 'smartstack://project',
  name: 'SmartStack Project Info',
  description: 'Current SmartStack project information, structure, and configuration',
  mimeType: 'text/markdown',
};

export async function getProjectInfoResource(config: Config): Promise<string> {
  const projectPath = config.smartstack.projectPath;
  const projectInfo = await detectProject(projectPath);
  const structure = await findSmartStackStructure(projectPath);

  const lines: string[] = [];

  lines.push('# SmartStack Project Information');
  lines.push('');
  lines.push('## Overview');
  lines.push('');
  lines.push(`| Property | Value |`);
  lines.push(`|----------|-------|`);
  lines.push(`| **Name** | ${projectInfo.name} |`);
  lines.push(`| **Version** | ${projectInfo.version} |`);
  lines.push(`| **Path** | \`${projectPath}\` |`);
  lines.push(`| **Git Repository** | ${projectInfo.isGitRepo ? 'Yes' : 'No'} |`);
  if (projectInfo.currentBranch) {
    lines.push(`| **Current Branch** | \`${projectInfo.currentBranch}\` |`);
  }
  lines.push(`| **.NET Project** | ${projectInfo.hasDotNet ? 'Yes' : 'No'} |`);
  lines.push(`| **EF Core** | ${projectInfo.hasEfCore ? 'Yes' : 'No'} |`);
  if (projectInfo.dbContextName) {
    lines.push(`| **DbContext** | \`${projectInfo.dbContextName}\` |`);
  }
  lines.push(`| **React Frontend** | ${projectInfo.hasReact ? 'Yes' : 'No'} |`);
  lines.push('');

  // Project Structure
  lines.push('## Project Structure');
  lines.push('');
  lines.push('```');
  lines.push(`${projectInfo.name}/`);
  if (structure.domain) {
    lines.push(`├── ${path.basename(structure.domain)}/          # Domain layer (entities)`);
  }
  if (structure.application) {
    lines.push(`├── ${path.basename(structure.application)}/     # Application layer (services)`);
  }
  if (structure.infrastructure) {
    lines.push(`├── ${path.basename(structure.infrastructure)}/  # Infrastructure (EF Core)`);
  }
  if (structure.api) {
    lines.push(`├── ${path.basename(structure.api)}/             # API layer (controllers)`);
  }
  if (structure.web) {
    lines.push(`└── web/smartstack-web/      # React frontend`);
  }
  lines.push('```');
  lines.push('');

  // .NET Projects
  if (projectInfo.csprojFiles.length > 0) {
    lines.push('## .NET Projects');
    lines.push('');
    lines.push('| Project | Path |');
    lines.push('|---------|------|');
    for (const csproj of projectInfo.csprojFiles) {
      const name = path.basename(csproj, '.csproj');
      const relativePath = path.relative(projectPath, csproj);
      lines.push(`| ${name} | \`${relativePath}\` |`);
    }
    lines.push('');
  }

  // Migrations info
  if (structure.migrations) {
    const migrationFiles = await findFiles('*.cs', {
      cwd: structure.migrations,
      ignore: ['*.Designer.cs'],
    });

    const migrations = migrationFiles
      .map(f => path.basename(f))
      .filter(f => !f.includes('ModelSnapshot') && !f.includes('.Designer.'))
      .sort();

    lines.push('## EF Core Migrations');
    lines.push('');
    lines.push(`**Location**: \`${path.relative(projectPath, structure.migrations)}\``);
    lines.push(`**Total Migrations**: ${migrations.length}`);
    lines.push('');

    if (migrations.length > 0) {
      lines.push('### Recent Migrations');
      lines.push('');
      const recent = migrations.slice(-5);
      for (const migration of recent) {
        lines.push(`- \`${migration.replace('.cs', '')}\``);
      }
      lines.push('');
    }
  }

  // Configuration
  lines.push('## Configuration');
  lines.push('');
  lines.push('### MCP Server Config');
  lines.push('');
  lines.push('```json');
  lines.push(JSON.stringify({
    smartstack: config.smartstack,
    conventions: {
      schemas: config.conventions.schemas,
      tablePrefixes: config.conventions.tablePrefixes,
      migrationFormat: config.conventions.migrationFormat,
    },
  }, null, 2));
  lines.push('```');
  lines.push('');

  // Quick Commands
  lines.push('## Quick Commands');
  lines.push('');
  lines.push('```bash');
  lines.push('# Build the solution');
  lines.push('dotnet build');
  lines.push('');
  lines.push('# Run API');
  lines.push(`cd ${structure.api ? path.relative(projectPath, structure.api) : 'src/Api'}`);
  lines.push('dotnet run');
  lines.push('');
  lines.push('# Run frontend');
  lines.push(`cd ${structure.web ? path.relative(projectPath, structure.web) : 'web'}`);
  lines.push('npm run dev');
  lines.push('');
  lines.push('# Create migration');
  lines.push('dotnet ef migrations add YYYYMMDD_Core_NNN_Description');
  lines.push('');
  lines.push('# Apply migrations');
  lines.push('dotnet ef database update');
  lines.push('```');
  lines.push('');

  // MCP Tools Available
  lines.push('## Available MCP Tools');
  lines.push('');
  lines.push('| Tool | Description |');
  lines.push('|------|-------------|');
  lines.push('| `validate_conventions` | Check code against AtlasHub conventions |');
  lines.push('| `check_migrations` | Analyze EF Core migrations for conflicts |');
  lines.push('| `scaffold_extension` | Generate service, entity, controller, or component |');
  lines.push('| `api_docs` | Get API endpoint documentation |');
  lines.push('');

  return lines.join('\n');
}
